import { useCallback, useEffect, useRef, useState } from "react";

const PAGE_SIZE = 20;
const DEFAULT_ORDER = ["Hogar", "Alimentación", "Ropa", "Salud", "Educación", "Transporte", "Entretenimiento", "Suscripciones", "Otros"];

export function useTransactionsData(api, { category, type, startDate, endDate, userId }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoryDict, setCategoryDict] = useState({});
  const request = useRef({ generation: 0, controller: null, offset: 0, busy: false, hasMore: false });

  const refreshCategories = useCallback(() => {
    const controller = new AbortController();
    const config = { signal: controller.signal };
    // These optional lookups must never block the ledger or its loading state.
    api.get("/categories", config).then(({ data }) => {
      if (controller.signal.aborted) return;
      const values = data?.categories || [];
      setCategories([...DEFAULT_ORDER.filter(c => values.includes(c)), ...values.filter(c => !DEFAULT_ORDER.includes(c))]);
    }).catch(() => {});
    api.get("/category-budgets", config).then(({ data }) => {
      if (controller.signal.aborted) return;
      const budgets = Array.isArray(data) ? data : data?.budgets || [];
      setCategoryDict(Object.fromEntries(budgets.map(b => [b.name, b.icon])));
    }).catch(() => {});
    return () => controller.abort();
  }, [api]);

  useEffect(() => refreshCategories(), [refreshCategories, userId]);

  const fetchData = useCallback(async (reset = true) => {
    if (!reset && (request.current.busy || !request.current.hasMore)) return;
    if (reset) {
      request.current.controller?.abort();
      request.current = {
        generation: request.current.generation + 1,
        controller: new AbortController(), offset: 0, busy: false, hasMore: false,
      };
      setTransactions([]);
      setHasMore(false);
      setLoading(true);
      setLoadingMore(false);
      setSummary(null);
      setSummaryLoading(true);
      setSummaryError(null);
    } else {
      setLoadingMore(true);
    }
    setError(null);
    const current = request.current;
    current.busy = true;
    const config = { signal: current.controller.signal };
    const isCurrent = () => request.current.generation === current.generation && !config.signal.aborted;
    const params = new URLSearchParams();
    if (category !== "all") params.set("category", category);
    if (type !== "all") params.set("type", type);
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    params.set("skip", String(current.offset));
    params.set("limit", String(PAGE_SIZE));

    if (reset) {
      const summaryParams = new URLSearchParams();
      if (startDate && endDate) {
        summaryParams.set("start_date", startDate);
        summaryParams.set("end_date", endDate);
      }
      // The dashboard defaults to the current month when dates are omitted.
      // "All" needs a server-side aggregate over every page of the ledger.
      const summaryUrl = !startDate && !endDate
        ? "/transactions/summary"
        : `/dashboard/summary?${summaryParams}`;
      api.get(summaryUrl, config).then(({ data }) => {
        if (isCurrent()) setSummary(data);
      }).catch(() => {
        if (isCurrent()) setSummaryError("No se pudo cargar el resumen.");
      }).finally(() => {
        if (isCurrent()) setSummaryLoading(false);
      });
    }

    try {
      const { data } = await api.get(`/transactions?${params}`, config);
      if (!isCurrent()) return;
      const records = data || [];
      current.offset += records.length;
      current.hasMore = records.length === PAGE_SIZE;
      setTransactions(previous => reset ? records : [...previous, ...records]);
      setHasMore(current.hasMore);
    } catch (_) {
      if (isCurrent()) {
        // Keep loaded pages on a pagination failure, but allow an explicit retry.
        setError("No se pudieron cargar los movimientos. Intenta de nuevo.");
        current.hasMore = false;
        setHasMore(false);
      }
    } finally {
      if (isCurrent()) {
        current.busy = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [api, category, type, startDate, endDate]);

  useEffect(() => {
    fetchData();
    return () => request.current.controller?.abort();
  }, [fetchData, userId]);

  return {
    transactions, loading, loadingMore, hasMore, error, summary, summaryLoading,
    summaryError, categories, categoryDict, fetchData, refreshCategories,
  };
}
